/**
 * The phone Control Panel's project map. It opens only from the bottom
 * bar's Map button (MapViewContext) and takes no space while closed. Its
 * data loads only once it is open: the project list on first open, the map
 * summary (the projects of every pour matching the filter and search) on
 * open, on a filter or search change and on each refresh while it stays
 * open. One finger pans; the mouse wheel never zooms.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import CollapsibleMapPanel, { InvalidateMapSize } from '../CollapsibleMapPanel';
import { useMapView } from '../../context/MapViewContext';
import { poursAPI } from '../../services/pours';
import type { PourMapSummary } from '../../services/pours';
import projectsService from '../../services/projects';
import type { ProjectListItem } from '../../services/projects';

/** Leaflet's own controls at a 44px touch size: the zoom buttons (30px by
 *  default) and the popup's close button (24px), whose room the popup text
 *  keeps clear of. Selectors outrank leaflet.css. */
const TOUCH_CONTROLS_SX = {
  height: '100%',
  width: '100%',
  '& .leaflet-bar a.leaflet-control-zoom-in, & .leaflet-bar a.leaflet-control-zoom-out': {
    width: 44,
    height: 44,
    lineHeight: '44px',
    fontSize: 22,
  },
  '& .leaflet-container a.leaflet-popup-close-button': {
    width: 44,
    height: 44,
    fontSize: 24,
    lineHeight: '44px',
  },
  '& .leaflet-container .leaflet-popup-content': { marginRight: '44px' },
} as const;

const PHONE_MAP_HEIGHT = 240;
const TABLET_MAP_HEIGHT = 300;
const DEFAULT_CENTER: [number, number] = [-33.8688, 151.2093];

const projectIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="#1565C0" stroke="#FFFFFF" stroke-width="1.5"/>
      <circle cx="12.5" cy="12.5" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

/** Zoom in on one point (the newest pour's project). Keyed on the
 *  coordinates, so a refresh that brings the same point back never undoes
 *  the user's own pan and zoom. */
const FocusPosition: React.FC<{ position: [number, number] | null; zoom?: number }> = ({ position, zoom = 14 }) => {
  const map = useMap();
  const lat = position?.[0];
  const lng = position?.[1];
  useEffect(() => {
    if (lat == null || lng == null) return undefined;
    const timeout = setTimeout(() => {
      // Bail if the map was torn down before the delay elapsed
      if ((map as any)._mapPane) map.setView([lat, lng], zoom);
    }, 400);
    return () => clearTimeout(timeout);
  }, [map, lat, lng, zoom]);
  return null;
};

export interface ControlPanelMapProps {
  /** The list's status filter label ("In progress", "All", ...). */
  status: string;
  /** The list's (debounced) search. */
  search: string;
  /** Bumped by the list's poll and Refresh: reloads the summary while the
   *  map is open. */
  refreshSignal: number;
  /** "View pours" in a project's popup: search the list for it. */
  onViewPours: (projectName: string) => void;
}

const ControlPanelMap: React.FC<ControlPanelMapProps> = ({ status, search, refreshSignal, onViewPours }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const mapView = useMapView();
  const open = mapView.open;

  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [summary, setSummary] = useState<PourMapSummary | null>(null);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const projectsRequested = useRef(false);
  const summaryRequestRef = useRef(0);
  const mountedRef = useRef(true);

  // A later answer for an unmounted page is dropped.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      summaryRequestRef.current += 1;
    };
  }, []);

  // The project list, once, the first time the map opens.
  useEffect(() => {
    if (!open || projectsRequested.current) return;
    projectsRequested.current = true;
    projectsService
      .listProjects()
      .then((list) => {
        if (mountedRef.current) setProjects(list || []);
      })
      .catch(() => {
        // Non-critical: the map simply has no pins.
      })
      .finally(() => {
        if (mountedRef.current) setProjectsLoaded(true);
      });
  }, [open]);

  // The pins follow the list's filter and search, and only while open.
  useEffect(() => {
    if (!open) return;
    const request = ++summaryRequestRef.current;
    poursAPI
      .getPourMapSummary({ status, search })
      .then((next) => {
        if (request === summaryRequestRef.current) setSummary(next);
      })
      .catch(() => {
        // Non-critical: the map keeps its last pins.
      })
      .finally(() => {
        if (request === summaryRequestRef.current) setSummaryLoaded(true);
      });
  }, [open, status, search, refreshSignal]);

  // Only projects with at least one matching pour are pinned.
  const pins = useMemo(() => {
    const norm = (s?: string | null) => (s ?? '').trim().toLowerCase();
    const listed = new Set((summary?.project_names ?? []).map(norm));
    return projects.filter(
      (p) =>
        p.latitude &&
        p.longitude &&
        !Number.isNaN(Number(p.latitude)) &&
        !Number.isNaN(Number(p.longitude)) &&
        listed.has(norm(p.project_name)),
    );
  }, [projects, summary]);

  const latest = summary?.latest_project_name
    ? pins.find((p) => p.project_name === summary.latest_project_name)
    : undefined;
  const focusProject = latest ?? pins[0];
  const focusPosition: [number, number] | null = focusProject
    ? [Number(focusProject.latitude), Number(focusProject.longitude)]
    : null;
  const ready = projectsLoaded && summaryLoaded;

  return (
    <CollapsibleMapPanel
      isMobile
      open={open}
      onToggle={mapView.toggle}
      mobileHeight={isTablet ? TABLET_MAP_HEIGHT : PHONE_MAP_HEIGHT}
      showSeam={false}
    >
      {(height) =>
        !open && !ready ? null : !ready ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'grey.100',
            }}
          >
            <CircularProgress size={28} aria-label={t('dashboard.mapView', 'Map View')} />
          </Box>
        ) : (
          <Box sx={TOUCH_CONTROLS_SX}>
            <MapContainer
              center={focusPosition ?? DEFAULT_CENTER}
              zoom={focusPosition ? 14 : 4}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
              attributionControl={false}
            >
              <InvalidateMapSize height={height} open={open} />
              <FocusPosition position={focusPosition} zoom={14} />
              <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {pins.map((project) => (
                <Marker
                  key={`proj-${project.project_id}`}
                  position={[Number(project.latitude), Number(project.longitude)]}
                  icon={projectIcon}
                >
                  <Popup>
                    <strong>{project.project_name}</strong>
                    {project.client && (
                      <>
                        <br />
                        {project.client}
                      </>
                    )}
                    {project.address && (
                      <>
                        <br />
                        {project.address}
                      </>
                    )}
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => onViewPours(project.project_name)}
                      sx={{ mt: 1, minHeight: 44, textTransform: 'none' }}
                    >
                      {t('mobile.controlPanel.viewPours', { defaultValue: 'View pours' })}
                    </Button>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </Box>
        )
      }
    </CollapsibleMapPanel>
  );
};

export default ControlPanelMap;
